import type { IntlShape } from 'react-intl'
import { getCurrentIntl } from 'i18n/util/currentIntl'

export function capitalise(str = '') {
  if (str.length > 0) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }
  return str
}
export function capitaliseAll(str = ''): string {
  const wordList = str.split(' ')
  if (wordList.length > 1) {
    const capitalisedWordList = wordList.map((word, index) => {
      if (index === 0 || (word.length > 2 && !capitaliseIgnoreSet.has(word))) {
        return capitalise(word)
      }
      return word
    })

    return capitalisedWordList.join(' ')
  }

  return str
}

export function contains(str: string, search: string) {
  return str.toLowerCase().includes(search.toLowerCase())
}

function isUpperCase(str: string) {
  const upperStr = str.toUpperCase()
  return str === upperStr
}

/**
 * Convert list of tokens to AP Title Case
 */
export const capitaliseIgnoreSet = new Set(['a', 'an', 'and', 'at', 'but', 'by', 'for', 'in', 'nor', 'of', 'on', 'or', 'so', 'the', 'to', 'up', 'yet'])

function toTitle(tokens: Array<string>) {
  return tokens.map((token, idx) => {
    return (idx === 0 || !capitaliseIgnoreSet.has(token)) ? capitalise(token) : token
  }).join(' ')
}

/**
 * Convert snake case string to AP Title Case
 *
 * @remarks
 * Does not support non-alpha chars
 */
export function snakeToTitle(str: string) {
  const tokens = str.split('_')
  return toTitle(tokens)
}

/**
  * Convert camel case string to AP Title Case
  *
  * @remarks
  * Does not support non-alpha chars
  */
export function camelToTitle(str: string) {
  const tokens: Array<string> = []
  let anchor = 0

  for (let i = 0; i < str.length; i++) {
    if (!isUpperCase(str.charAt(i))) { continue }
    let token = str.slice(anchor, i).toLowerCase()
    if (i === 0 || capitaliseIgnoreSet.has(token)) { token = capitalise(token) }
    tokens.push(token)
    anchor = i
  }

  // loop doesn't handle adding the last token - add it explicitly
  tokens.push(str.slice(anchor, str.length))
  return toTitle(tokens)
}

const KILO = 1024

function formatByteCountLabel(intl: IntlShape, formattedValue: string, byteCount: number): string {
  return intl.formatMessage(
    {
      id: 'ZrFSsHIaY-iIDqcdV_uW',
      defaultMessage: '{formattedValue} {byteCount, plural, one {Byte} other {Bytes}}',
      description: 'Localized file size label for raw byte counts shown in upload and attachment size displays',
    },
    {
      formattedValue,
      byteCount,
    },
  )
}

const PREFIXED_BYTE_FORMATTERS: ReadonlyArray<(intl: IntlShape, formattedValue: string) => string> = [
  (intl, formattedValue) => intl.formatMessage(
    {
      id: 'Y_OJJBWVfjeQlt6FhbQV',
      defaultMessage: '{formattedValue} KB',
      description: 'Localized file size label for values measured in kilobytes in upload and attachment size displays',
    },
    { formattedValue },
  ),
  (intl, formattedValue) => intl.formatMessage(
    {
      id: 'udlE5WmpGsJqPKjvU68r',
      defaultMessage: '{formattedValue} MB',
      description: 'Localized file size label for values measured in megabytes in upload and attachment size displays',
    },
    { formattedValue },
  ),
  (intl, formattedValue) => intl.formatMessage(
    {
      id: 'FwipXe2pvXx9xsSlhxG5',
      defaultMessage: '{formattedValue} GB',
      description: 'Localized file size label for values measured in gigabytes in upload and attachment size displays',
    },
    { formattedValue },
  ),
  (intl, formattedValue) => intl.formatMessage(
    {
      id: 'w9_mlmanv-JkwN9EPYjN',
      defaultMessage: '{formattedValue} TB',
      description: 'Localized file size label for values measured in terabytes in upload and attachment size displays',
    },
    { formattedValue },
  ),
  (intl, formattedValue) => intl.formatMessage(
    {
      id: 'UEKs6kWYIqo4yyj_GgSc',
      defaultMessage: '{formattedValue} PB',
      description: 'Localized file size label for values measured in petabytes in upload and attachment size displays',
    },
    { formattedValue },
  ),
  (intl, formattedValue) => intl.formatMessage(
    {
      id: 'HPYsum7i-37etfnV64ra',
      defaultMessage: '{formattedValue} EB',
      description: 'Localized file size label for values measured in exabytes in upload and attachment size displays',
    },
    { formattedValue },
  ),
  (intl, formattedValue) => intl.formatMessage(
    {
      id: 'AqY7p8ORyIo5l5p001El',
      defaultMessage: '{formattedValue} ZB',
      description: 'Localized file size label for values measured in zettabytes in upload and attachment size displays',
    },
    { formattedValue },
  ),
  (intl, formattedValue) => intl.formatMessage(
    {
      id: 'i0DXyLKVAJ7xCZ6cZhit',
      defaultMessage: '{formattedValue} YB',
      description: 'Localized file size label for values measured in yottabytes in upload and attachment size displays',
    },
    { formattedValue },
  ),
] as const

export function formatBytes(
  bytes: number,
  decimalPoints = 2,
): string {
  const intl = getCurrentIntl()
  if (bytes === 0) {
    return formatByteCountLabel(
      intl,
      intl.formatNumber(0, { maximumFractionDigits: decimalPoints }),
      bytes,
    )
  }

  const i = Math.floor(Math.log(bytes) / Math.log(KILO))
  const formattedValue = intl.formatNumber(bytes / Math.pow(KILO, i), {
    maximumFractionDigits: decimalPoints,
  })

  if (i === 0) {
    return formatByteCountLabel(intl, formattedValue, bytes)
  }

  return PREFIXED_BYTE_FORMATTERS[i - 1](intl, formattedValue)
}

/**
 * Formats a list of strings using Intl list semantics.
 * Accepts semantic types (`conjunction` / `disjunction`) and legacy string aliases (`and` / `or`).
 */
export function arrayToStringListFormat(
  array: Array<string>,
  joinType: 'conjunction' | 'disjunction' | 'and' | 'or' = 'or',
  intl: IntlShape,
) {
  const type = joinType === 'and'
    ? 'conjunction'
    : joinType === 'or'
      ? 'disjunction'
      : joinType
  const formatter = new Intl.ListFormat(intl.locale || 'en', { type })
  return formatter.format(array)
}

export function areStringsEqual(str1: string, str2: string): boolean {
  if (!str1 || !str2) return false
  return str1.toLowerCase().trim() === str2.toLowerCase().trim()
}

export function isString(val: unknown): val is string {
  return typeof val === 'string'
}

export function capitaliseFirstLetterOnly<T extends string>(str: T): Capitalize<T> {
  if (str?.length > 0) {
    return (str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()) as Capitalize<T>
  }
  return str as Capitalize<T>
}

export function toTitleCase(str: string): string {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
}

/**
 * Convert a string to PascalCase removing non-alphabetical characters
 * @param str The string to convert
 * @returns The PascalCase string
 */
export function toPascalCase(str: string): string {
  return str
    .replace(/[^a-zA-Z]+/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('')
}

/**
 * Ensures a sentence ends with a full stop, leaving existing terminal punctuation untouched
 * @param str The sentence to normalise
 * @returns The sentence with trailing whitespace removed and a full stop appended when no terminal punctuation is present; empty input stays empty
 */
export function ensureTrailingFullStop(str: string): string {
  const trimmed = str.trimEnd()
  if (!trimmed || /[.!?…]$/.test(trimmed)) return trimmed
  return `${trimmed}.`
}

/**
 * Safely converts a string to a number, handling edge cases:
 * - Returns undefined for empty string, null, or undefined
 * - Returns undefined for non-numeric values (NaN)
 * - Preserves valid numbers including 0
 */
export function safeNumberConversion(str: string): number | undefined {
  if (str === '') return undefined
  const num = Number(str)
  if (Number.isNaN(num)) return undefined
  return num
}

/**
 * Formats a number of guests into a human-readable string
 * @param totalGuests The total number of guests
 * @returns A formatted string like "2 people" or "1 person", or undefined if totalGuests is 0 or falsy
 * @example
 * formatGuestsDisplay(1) // "1 person"
 * formatGuestsDisplay(2) // "2 people"
 * formatGuestsDisplay(0) // undefined
 */
export function formatGuestsDisplay(totalGuests: number, intl: IntlShape): string | undefined {
  if (!totalGuests) return undefined

  return intl.formatMessage(
    {
      id: 'u7KQwsPM34ywc8Wa6mTP',
      defaultMessage: '{totalGuests, plural, one {# person} other {# people}}',
      description: 'Formatted guest count with singular or plural guest noun on charter offer pricing',
    },
    { totalGuests },
  )
}

export function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
}

export function isSlug(value: string): boolean {
  return /^[a-z0-9]+(_[a-z0-9]+)*$/.test(value)
}
